import { useState } from "react";
import { Box, Flex, Text, Button, Stack, HStack, Badge, Textarea } from "@chakra-ui/react";
import { Copy, CheckCircle2, AlertTriangle, UserCheck } from "lucide-react";
import { apiPost } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { colors } from "@/theme/colors";

const ROLE_LABELS = { org_admin: "Org Admin", supervisor: "Supervisor", field_employee: "Field Employee" };

const parseLines = (raw) =>
  raw.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [email, ...rest] = l.split(",").map((p) => p.trim());
    return { email, full_name: rest.join(" ").trim() || null };
  });

const rowCategory = (r) => r.category || (r.ok ? "invited" : "failed");

const ResultRow = ({ r, companyId, flash }) => {
  const cat = rowCategory(r);
  const [copying, setCopying] = useState(false);
  const icon = cat === "invited" ? <CheckCircle2 size={14} color={colors.resolvedText} />
    : cat === "skipped" ? <UserCheck size={14} color={colors.textSecondary} />
    : <AlertTriangle size={14} color={colors.poppy} />;
  const label = cat === "invited"
    ? (r.attached_existing ? "Added — existing account, no invite link needed"
      : r.emailed ? "Invited — email sent"
      : "Invited — email not sent; use Copy link to hand-deliver")
    : (r.message || (cat === "skipped" ? "Already active" : "Failed"));
  const copyFresh = async () => {
    setCopying(true);
    try {
      const res = await apiPost("/workers/mint-invite-link", { email: r.email, company_id: companyId });
      await navigator.clipboard?.writeText(`${window.location.origin}/accept-invite?token_hash=${res.hashed_token}&type=recovery`);
      flash("success", `Fresh invite link for ${r.email} copied — valid for 1 hour.`);
    } catch (e) { flash("error", e.message); } finally { setCopying(false); }
  };
  return (
    <Flex justify="space-between" align="center" gap="2" py="2" borderBottom="1px solid" borderColor={colors.border}
      _last={{ borderBottom: "none" }} data-testid={`bulk-result-${r.email}`}>
      <HStack gap="2" minW="0">
        {icon}
        <Box minW="0">
          <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} truncate>{r.email}</Text>
          <Text fontSize="xs" color={cat === "failed" ? colors.poppy : colors.textSecondary}>{label}</Text>
        </Box>
      </HStack>
      {cat === "invited" && !r.attached_existing && (
        <Button size="xs" variant="outline" loading={copying} onClick={copyFresh} data-testid={`bulk-copy-link-${r.email}`}>
          <Copy size={12} /> Copy link
        </Button>
      )}
    </Flex>
  );
};

export const BulkInviteModal = ({ open, onClose, companyId, flash, onDone }) => {
  const [raw, setRaw] = useState("");
  const [role, setRole] = useState("field_employee");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const rows = parseLines(raw);

  const submit = async () => {
    setBusy(true);
    setSubmitError(null);
    try {
      const res = await apiPost("/workers/invite-bulk", {
        company_id: companyId, role,
        redirect_to: `${window.location.origin}/accept-invite`,
        invites: rows.map((r) => ({ email: r.email, full_name: r.full_name })),
      });
      setResults(res);
      const parts = [`${res.invited} invited`];
      if (res.skipped) parts.push(`${res.skipped} already active`);
      if (res.failed) parts.push(`${res.failed} failed`);
      flash(res.failed ? "error" : "success", `${parts.join(", ")}${res.failed ? " — details below." : "."}`);
      onDone?.();
    } catch (e) {
      // Inline, in the form, non-dismissing while the modal is open — never a banner
      // behind the modal. A whole-request failure means NO ONE was invited (per-row
      // problems come back in the results list instead), so say exactly that.
      setSubmitError(
        (e.status >= 500 || !e.status)
          ? "The invite service was briefly unavailable, so no one in this batch was invited. Your list is still here — please press Invite again."
          : (e.message || "Could not send these invites. Please try again."));
    } finally { setBusy(false); }
  };

  const close = () => { setResults(null); setRaw(""); setSubmitError(null); onClose(); };

  return (
    <Modal open={open} onClose={close} title="Bulk invite" testId="bulk-invite-modal">
      {results ? (
        <Stack gap="2">
          <HStack gap="2" flexWrap="wrap">
            <Badge bg={colors.resolvedBg} color={colors.resolvedText} borderRadius="full" px="2.5" data-testid="bulk-invited-count">{results.invited} invited</Badge>
            <Badge bg={colors.cream} color={colors.textSecondary} borderRadius="full" px="2.5" data-testid="bulk-skipped-count">{results.skipped || 0} already active</Badge>
            <Badge bg={results.failed ? "#FDE8E8" : colors.cream} color={results.failed ? colors.poppy : colors.textSecondary} borderRadius="full" px="2.5" data-testid="bulk-failed-count">{results.failed} failed</Badge>
          </HStack>
          <Box maxH="320px" overflowY="auto" data-testid="bulk-results-list">
            {results.results.map((r, i) => (
              <ResultRow key={`${r.email}-${i}`} r={r} companyId={companyId} flash={flash} />
            ))}
          </Box>
          <Button onClick={close} data-testid="bulk-done-btn">Done</Button>
        </Stack>
      ) : (
        <Stack gap="3">
          <Box>
            <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>
              One person per line — email, or "email, Full Name"
            </Text>
            <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={8}
              placeholder={"jane@site.com, Jane Doe\nmike@site.com"}
              bg={colors.white} borderColor={colors.border} fontSize="sm" data-testid="bulk-emails-input" />
            <Text fontSize="xs" color={colors.textSecondary} mt="1" data-testid="bulk-parsed-count">
              {rows.length} {rows.length === 1 ? "person" : "people"} · adding a name also creates their worker record
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Role for everyone in this batch</Text>
            <Box as="select" value={role} onChange={(e) => setRole(e.target.value)}
              w="full" px="3" py="2.5" borderRadius="8px" border="1px solid" borderColor={colors.border} bg={colors.white} fontSize="sm" data-testid="bulk-role-select">
              {Object.entries(ROLE_LABELS).map(([r, l]) => <option key={r} value={r}>{l}</option>)}
            </Box>
          </Box>
          {submitError && (
            <Text fontSize="sm" color={colors.poppy} data-testid="bulk-submit-error">{submitError}</Text>
          )}
          <Button onClick={submit} loading={busy} disabled={!rows.length || rows.length > 100} data-testid="bulk-send-btn">
            Invite {rows.length || ""} {rows.length === 1 ? "person" : "people"}
          </Button>
          {rows.length > 100 && <Text fontSize="xs" color={colors.poppy}>Maximum 100 per batch — split the list.</Text>}
        </Stack>
      )}
    </Modal>
  );
};
